import React, { useState } from "react";
import StarRating from "./StarRating";

const RatingModal = ({ isOpen, onClose, onSubmit, product, existingRating = null }) => {
  const [rating, setRating] = useState(existingRating?.rating || 0);
  const [review, setReview] = useState(existingRating?.review || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update state when existingRating changes
  React.useEffect(() => {
    if (existingRating) {
      setRating(existingRating.rating || 0);
      setReview(existingRating.review || "");
    } else {
      setRating(0);
      setReview("");
    }
  }, [existingRating]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        rating,
        review,
        product_id: product.pr_id || product.id,
        product_name: product.pr_name || product.name,
        isUpdate: !!existingRating
      });
      onClose();
    } catch (error) {
      console.error("Failed to submit rating:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto border border-gray-200">
        {/* Header */}
        <div className="p-4 text-white rounded-t-lg" style={{ background: 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))' }}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold">
                    {existingRating ? "Update Rating" : "Rate Product"}
                  </h3>
                  <p className="text-white/90 text-sm">Share your experience</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-300 flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4">
          {/* Product Information */}
          <div className="mb-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-white p-1">
                <img
                  src={(() => {
                    const isContactLens = Boolean(product.contact_lens_images);
                    const images = isContactLens
                      ? product.contact_lens_images?.split(",").filter(img => img.trim()) || []
                      : product.pr_image?.split(",").filter(img => img.trim()) || [];
                    const selectedImage = images[product.selected_image_to_show || 0] || images[0];
                    return selectedImage ? `https://newpos.spectsgenie.com/${selectedImage}` : null;
                  })()}
                  alt={product.pr_name || product.name || "Product"}
                  className="w-full h-full object-cover rounded"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDMTA4LjI4NCA3MCA5NS4wMDAwIDc1IDk1IDgzLjMzMzNWMTE2LjY2N0M5NSAxMjUgMTA4LjI4NCAxMzAgMTAwIDEzMEM5MS43MTU3IDEzMCA3OC4zMzMzIDEyNSA3OC4zMzMzIDExNi42NjdWODMuMzMzM0M3OC4zMzMzIDc1IDkxLjcxNTcgNzAgMTAwIDcwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-gray-900 text-sm mb-1 line-clamp-2">
                  {product.pr_name || product.name || "Product Name"}
                </h4>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-bold" style={{ color: 'gold' }}>
                    ₹{product.price || product.pr_price || "N/A"}
                  </span>
                </div>
                {(product.lens_material || product.lens_package_name) && (
                  <div className="space-y-1 text-xs text-gray-600">
                    {product.lens_material && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Lens:</span>
                        <span>{product.lens_material}</span>
                      </div>
                    )}
                    {product.lens_package_name && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Package:</span>
                        <span>{product.lens_package_name}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Rating Section */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                How would you rate this product?
              </label>
              <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <div className="mb-3">
                  <StarRating
                    rating={rating}
                    onRatingChange={setRating}
                    readonly={false}
                    size={24}
                  />
                </div>
                <div className="text-center">
                  {rating > 0 ? (
                    <div className="space-y-1">
                      <span className="text-base font-bold" style={{ color: 'gold' }}>
                        {rating}/5 Stars
                      </span>
                      <p className="text-xs text-gray-600">
                        {rating === 5 ? "Excellent!" : rating >= 4 ? "Great!" : rating >= 3 ? "Good" : rating >= 2 ? "Fair" : "Poor"}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Tap a star to rate</p>
                  )}
                </div>
              </div>
            </div>

            {/* Review Section */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                Review (Optional)
              </label>
              <div className="relative">
                <textarea
                  value={review}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setReview(e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-300 text-gray-900 placeholder-gray-400"
                  rows="3"
                  placeholder="Share your experience with this product..."
                  maxLength={500}
                />
                <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-1">
                  {review.length}/500
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={rating === 0 || isSubmitting}
                className="flex-1 px-4 py-2 text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 font-medium text-sm shadow-md hover:shadow-lg"
                style={{ 
                  background: rating === 0 || isSubmitting ? '#9CA3AF' : 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))'
                }}
              >
                {isSubmitting && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {existingRating ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RatingModal;